// CRM 2.0 · M12: Templates › Company defaults (DC-224 / DC-225). The template a document kind uses when the
// booking's project has no link of its own in Project › Template Mapping; today only the top sheet has one.
// Removing a default first lists the projects that use it (no link of their own): their bookings then stop with
// "No … template linked" until a project link or a new default is set (backend: services/documents/templates/linking.js).
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import cls from "./Templates.module.css";
import { canDo } from "./templateCommon";
import { getCompanyDefaults, saveCompanyDefault } from "../../Services/Templates";
import { errorTost, successTost } from "../../Services/Toast";

function RemovePopup({ row, onCancel, onConfirm }) {
  const [busy, setBusy] = useState(false);
  const projects = row.projectsOnDefault;
  async function go() {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className={cls.mask} role="dialog" aria-modal="true" aria-label="Remove the company default">
      <div className={cls.modal} style={{ maxWidth: "56rem" }}>
        <div className={cls.modalHead}>
          <h2>Remove the company default {row.kind.name}?</h2>
          {projects.length ? (
            <p className={cls.hint}>
              {projects.length} {projects.length === 1 ? "project has" : "projects have"} no {row.kind.name} link of their own and use{projects.length === 1 ? "s" : ""} this
              default. After removing it, their bookings get no {row.kind.name} until one is linked in Project › Template Mapping or a new default is set.
            </p>
          ) : (
            <p className={cls.hint}>Every project has its own {row.kind.name} link, so no booking uses this default.</p>
          )}
        </div>
        {projects.length > 0 && (
          <div style={{ maxHeight: "28rem", overflowY: "auto", padding: "0 2.4rem" }}>
            <ul style={{ margin: 0, paddingLeft: "2rem" }}>
              {projects.map((p) => <li key={p.projectId}>{p.projectName}</li>)}
            </ul>
          </div>
        )}
        <div className={cls.modalFoot}>
          <button className={cls.btn} onClick={onCancel} disabled={busy}>Cancel</button>
          <button className={`${cls.btn} ${cls.dangerFill}`} onClick={go} disabled={busy}>{busy ? "Removing…" : "Remove default"}</button>
        </div>
      </div>
    </div>
  );
}

function CompanyDefaults() {
  const user = useSelector((state) => state.Auth.user);
  const allowed = canDo(user, "Template Linking");
  const [rows, setRows] = useState(null);
  const [chosen, setChosen] = useState({}); // kind key -> docTemplateId picked in the dropdown
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [problem, setProblem] = useState(null);

  function show(list) {
    setRows(list);
    setChosen(Object.fromEntries(list.map((r) => [r.kind.key, r.current ? r.current.docTemplateId : ""])));
  }

  useEffect(() => {
    if (!allowed) return;
    getCompanyDefaults().then(show).catch((e) => setProblem(e.message));
  }, [allowed]);

  async function save(row, docTemplateId, confirmRemove) {
    setBusy(true);
    try {
      show(await saveCompanyDefault(row.kind.key, docTemplateId, confirmRemove));
      successTost(docTemplateId ? `Company default ${row.kind.name} saved` : `Company default ${row.kind.name} removed`);
      setRemoving(null);
    } catch (e) {
      errorTost(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (!allowed) return <div className={cls.panel}><p className={cls.hint}>You need Templates › Template Linking access to see or change the company defaults.</p></div>;
  if (problem) return <div className={cls.panel}><p className={cls.hint}>{problem}</p></div>;
  if (!rows) return <div className={cls.panel}><p className={cls.hint}>Loading…</p></div>;

  return (
    <div className={cls.panel}>
      <h2 className={cls.panelTitle}>Company defaults</h2>
      <p className={cls.hint} style={{ marginTop: 0 }}>
        The template a booking uses when its project has no link of its own in Project › Template Mapping. A project link always wins.
      </p>
      <div className={cls.tblw}>
        <table className={cls.tbl}>
          <thead><tr><th>Document</th><th>Company default now</th><th>Projects using it</th><th>Template</th><th className={cls.last} /></tr></thead>
          <tbody>
            {!rows.length && <tr><td colSpan={5} className={cls.empty}>No document kind has a company default.</td></tr>}
            {rows.map((r) => {
              const picked = chosen[r.kind.key] || "";
              const changed = picked !== (r.current ? r.current.docTemplateId : "");
              const notLive = r.current && r.current.status !== "LIVE";
              return (
                <tr key={r.kind.key}>
                  <td className={cls.main}>{r.kind.name}</td>
                  <td style={notLive || !r.current ? { color: "var(--err)" } : undefined}>
                    {r.current ? `${r.current.template || "Template"}${notLive ? ` (${String(r.current.status || "").toLowerCase()}: choose a Live one)` : ""}` : "None"}
                  </td>
                  <td>{r.projectsOnDefault.length}</td>
                  <td>
                    <select className={cls.sel} value={picked} aria-label={`${r.kind.name} company default`}
                      onChange={(e) => setChosen((c) => ({ ...c, [r.kind.key]: e.target.value }))}>
                      <option value="">Choose a template…</option>
                      {r.liveTemplates.map((t) => <option key={t.docTemplateId} value={t.docTemplateId}>{t.template}</option>)}
                      {notLive && <option value={r.current.docTemplateId}>{r.current.template}</option>}
                    </select>
                    {!r.liveTemplates.length && <div className={cls.hint}>No Live {r.kind.name} template yet: approve one in Templates first.</div>}
                  </td>
                  <td className={cls.last} style={{ whiteSpace: "nowrap" }}>
                    <button className={`${cls.btn} ${cls.primary}`} disabled={busy || !picked || !changed} onClick={() => save(r, picked, false)}>Save</button>
                    {r.current && <button className={`${cls.btn} ${cls.danger}`} style={{ marginLeft: ".8rem" }} disabled={busy} onClick={() => setRemoving(r)}>Remove</button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {removing && <RemovePopup row={removing} onCancel={() => setRemoving(null)} onConfirm={() => save(removing, null, true)} />}
    </div>
  );
}

export default CompanyDefaults;
